import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Menu, X, Mail, MapPin, ChevronDown } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";

const LOGO_URL = "/img/logo.png";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/summer-camp", label: "Summer Camp" },
  { to: "/afterschool", label: "Afterschool" },
  { to: "/homeschool", label: "Homeschool" },
  { to: "/framework", label: "Framework" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/partner", label: "Partner" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact" },
];

const desktopPrimary = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
];
const programsMenu = [
  { to: "/programs", label: "All Programs" },
  { to: "/summer-camp", label: "Summer Camp" },
  { to: "/afterschool", label: "Afterschool" },
  { to: "/homeschool", label: "Homeschool" },
  { to: "/framework", label: "Framework" },
];
const desktopSecondary = [
  { to: "/get-involved", label: "Get Involved" },
  { to: "/partner", label: "Partner" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact" },
];

function Logo({ light = false }) {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
      <span className={`inline-flex shrink-0 ${light ? "bg-white rounded-xl p-1" : ""}`}>
        <img
          src={LOGO_URL}
          alt="LP Foundation logo"
          className={`object-contain transition-transform duration-300 group-hover:scale-105 ${light ? "h-11 w-11" : "h-14 w-14"}`}
        />
      </span>
      <div className="leading-tight">
        <span className={`block font-display font-extrabold text-lg tracking-tight ${light ? "text-white" : "text-brandnavy"}`}>
          LP Foundation
        </span>
        <span className={`block text-[10px] tracking-[0.22em] uppercase ${light ? "text-slate-300" : "text-slate-500"}`}>
          Learn · Grow · Lead · Impact
        </span>
      </div>
    </Link>
  );
}

export default function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setProgOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-white/40 backdrop-blur-md"
        }`}
      >
        <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Logo />
          <div className="hidden xl:flex items-center gap-0.5">
            {desktopPrimary.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-accent" : "text-slate-700 hover:text-brandnavy hover:bg-slate-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            <div className="relative" onMouseEnter={() => setProgOpen(true)} onMouseLeave={() => setProgOpen(false)}>
              <button
                data-testid="nav-programs-menu"
                onClick={() => setProgOpen((v) => !v)}
                className="flex items-center gap-1 whitespace-nowrap px-2.5 py-2 rounded-full text-sm font-medium text-slate-700 transition-colors duration-200 hover:text-brandnavy hover:bg-slate-100"
              >
                Programs <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${progOpen ? "rotate-180" : ""}`} />
              </button>
              {progOpen && (
                <div className="absolute left-0 top-full pt-2">
                  <div data-testid="programs-dropdown" className="w-56 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl">
                    {programsMenu.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `block rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                            isActive ? "bg-orange-50 text-accent" : "text-slate-700 hover:bg-slate-100"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {desktopSecondary.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-2.5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-accent" : "text-slate-700 hover:text-brandnavy hover:bg-slate-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/support"
              data-testid="nav-donate-button"
              className="ml-1.5 inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:bg-orange-400 hover:-translate-y-0.5 hover:shadow-lg whitespace-nowrap"
            >
              <Heart className="h-4 w-4 fill-current" /> Donate
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden grid h-10 w-10 place-items-center rounded-xl text-slate-800 hover:bg-slate-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </nav>
        {open && (
          <div data-testid="mobile-menu" className="xl:hidden border-t border-slate-200 bg-white px-5 py-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-medium ${
                    isActive ? "bg-orange-50 text-accent" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/support"
              data-testid="mobile-donate-button"
              className="mt-2 flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3 font-semibold text-white"
            >
              <Heart className="h-4 w-4 fill-current" /> Donate
            </Link>
          </div>
        )}
      </header>

      <main className="flex-1 pt-[72px]">{children}</main>

      <footer data-testid="site-footer" className="bg-brandnavy text-slate-300">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Logo light />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              Preparing future leaders for real-world success through SEAA — Science, Engineering, Athletics &amp; Arts. We
              build confidence, character, and opportunity, one young person at a time.
            </p>
            <Link
              to="/support"
              data-testid="footer-donate-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-orange-400 hover:-translate-y-0.5"
            >
              <Heart className="h-4 w-4 fill-current" /> Support Our Mission
            </Link>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-display font-semibold text-white text-sm tracking-wide uppercase">Explore</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {navItems.map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="text-slate-400 hover:text-white transition-colors">
                    {i.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/support" className="text-slate-400 hover:text-white transition-colors">
                  Support
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-slate-400 hover:text-white transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/survey" className="text-slate-400 hover:text-white transition-colors">
                  Share Feedback
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-display font-semibold text-white text-sm tracking-wide uppercase">Get In Touch</h4>
            <ul className="mt-5 space-y-3 text-sm text-slate-400">
              <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-accent" /> info@lpfoundation.world</li>
              <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-accent" /> Serving our community</li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <NewsletterSignup />
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} LP Foundation. All rights reserved.</span>
            <span>A 501(c)(3) nonprofit organization.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
